import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { 
  Send, 
  Loader2, 
  User, 
  Briefcase, 
  Lock, 
  Check, 
  CheckCheck
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { auth } from '../utils/auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ChatMessages = ({ caseId }) => {
  const user = auth.getUser();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [isInternalNote, setIsInternalNote] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    fetchMessages();
    const interval = setInterval(fetchMessages, 5000);
    return () => clearInterval(interval);
  }, [caseId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchMessages = async () => {
    try {
      const token = auth.getToken();
      const response = await axios.get(`${API}/inbox/messages`, {
        params: { case_id: caseId, channel: 'CHAT' },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const inboxMessages = (response.data.messages || []).map((msg: Record<string, unknown>) => {
        const fromObj = msg.from as Record<string, unknown> | undefined;
        return {
          id: msg.id,
          content: msg.body,
          sender_id: fromObj?.user_id || fromObj?.email,
          sender_role: fromObj?.type,
          sender_name: fromObj?.name,
          message_type: msg.channel,
          created_at: msg.created_at
        };
      });
      
      setMessages(inboxMessages);
    } catch (error) {
      console.error('Error fetching messages:', error);
      if (loading) {
        toast.error('Kunne ikke laste meldinger');
      }
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    const messageContent = newMessage;
    setNewMessage('');
    setSending(true);

    const tempMessage = {
      id: 'temp-' + Date.now(),
      content: messageContent,
      sender_id: user.id,
      sender_role: user.role,
      sender_name: user.name,
      message_type: isInternalNote ? 'INTERNAL_NOTE' : 'TEXT',
      created_at: new Date().toISOString(),
      status: 'sending'
    };
    setMessages(prev => [...prev, tempMessage]);

    try {
      const token = auth.getToken();
      await axios.post(
        `${API}/inbox/messages`,
        {
          case_id: caseId,
          channel: 'CHAT',
          body: messageContent,
          subject: isInternalNote ? 'Internt notat' : null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setIsInternalNote(false);
      setTimeout(fetchMessages, 500);
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Kunne ikke sende melding');
      setMessages(prev => prev.filter(m => m.id !== tempMessage.id));
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <Loader2 className="w-6 h-6 text-gray-400 animate-spin mx-auto mb-3" />
          <p className="text-sm text-gray-500">Laster meldinger...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-stone-50">
      {/* Messages List */}
      <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4">
        {messages.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-4">
              <Send className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Ingen meldinger ennå</h3>
            <p className="text-gray-500 text-sm">Start samtalen med klienten</p>
          </div>
        ) : (
          messages.map((msg, index) => {
            const isInternalNote = msg.message_type === 'INTERNAL_NOTE';
            const isOwnMessage = msg.sender_id === user.id;
            const showAvatar = index === 0 || messages[index - 1].sender_id !== msg.sender_id;
            const isLastInGroup = index === messages.length - 1 || messages[index + 1].sender_id !== msg.sender_id;
            
            return (
              <div
                key={msg.id}
                className={`flex gap-3 ${
                  isOwnMessage ? 'flex-row-reverse' : 'flex-row'
                } ${showAvatar ? 'mt-6' : 'mt-1'}`}
              >
                {/* Avatar */}
                {showAvatar ? (
                  <div
                    className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      isInternalNote
                        ? 'bg-amber-100 text-amber-700'
                        : msg.sender_role === 'LAWYER'
                        ? 'bg-gray-800 text-white'
                        : 'bg-gray-200 text-gray-600'
                    }`}
                  >
                    {isInternalNote ? (
                      <Lock className="w-4 h-4" />
                    ) : msg.sender_role === 'LAWYER' ? (
                      <Briefcase className="w-4 h-4" />
                    ) : (
                      <User className="w-4 h-4" />
                    )}
                  </div>
                ) : (
                  <div className="w-9 flex-shrink-0"></div>
                )}

                {/* Message bubble */}
                <div className={`max-w-[75%] ${isOwnMessage ? 'items-end' : 'items-start'}`}>
                  {/* Sender name */}
                  {showAvatar && !isInternalNote && msg.sender_name && (
                    <p className={`text-xs font-medium mb-1 px-1 ${
                      isOwnMessage ? 'text-right' : 'text-left'
                    } text-gray-500`}>
                      {msg.sender_name}
                    </p>
                  )}
                  
                  {/* Message Content */}
                  <div
                    className={`rounded-xl px-4 py-2.5 ${
                      isLastInGroup ? '' : 'mb-0.5'
                    } ${
                      isInternalNote
                        ? 'bg-amber-50 text-gray-900 border border-amber-200'
                        : isOwnMessage
                        ? 'bg-gray-900 text-white'
                        : 'bg-white text-gray-900 border border-gray-200'
                    }`}
                  >
                    {isInternalNote && (
                      <div className="flex items-center gap-1.5 mb-2 pb-2 border-b border-amber-200">
                        <Lock className="w-3 h-3 text-amber-600" />
                        <p className="text-xs font-medium text-amber-600">Internt notat</p>
                      </div>
                    )}
                    <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">{msg.content}</p>
                  </div>

                  {/* Timestamp & Status */}
                  {isLastInGroup && (
                    <div className={`flex items-center gap-1 mt-1 px-1 ${
                      isOwnMessage ? 'flex-row-reverse' : 'flex-row'
                    }`}>
                      <p className="text-xs text-gray-400">
                        {new Date(msg.created_at).toLocaleString('no-NO', {
                          day: '2-digit',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                      
                      {isOwnMessage && (
                        <div className="text-gray-400">
                          {msg.status === 'sending' ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : msg.status === 'read' ? (
                            <CheckCheck className="w-3 h-3 text-gray-500" />
                          ) : (
                            <Check className="w-3 h-3" />
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
        
        {/* Typing Indicator */}
        {isTyping && (
          <div className="flex gap-3">
            <div className="w-9 h-9 bg-gray-200 rounded-lg flex items-center justify-center">
              <User className="w-4 h-4 text-gray-600" />
            </div>
            <div className="bg-white border border-gray-200 rounded-xl px-4 py-2.5">
              <div className="flex gap-1">
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="border-t border-gray-200 bg-white">
        <div className="p-4">
          {/* Internal Note Toggle */}
          {user.role === 'LAWYER' && (
            <div className="mb-3">
              <button
                type="button"
                onClick={() => setIsInternalNote(!isInternalNote)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                  isInternalNote
                    ? 'bg-amber-50 text-amber-700 border border-amber-200'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-transparent'
                }`}
              >
                <Lock className="w-3.5 h-3.5" />
                {isInternalNote ? 'Internt notat aktivert' : 'Internt notat'}
              </button>
            </div>
          )}
          
          <form onSubmit={sendMessage} className="flex gap-3">
            <div className="flex-1 relative">
              <textarea
                ref={inputRef}
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage(e);
                  }
                }}
                placeholder={isInternalNote ? "Skriv et internt notat..." : "Skriv en melding..."}
                className="w-full resize-none border border-gray-200 focus:border-gray-400 rounded-xl px-4 py-3 pr-12 focus:outline-none focus:ring-2 focus:ring-gray-200 transition-all bg-gray-50 text-sm"
                rows={2}
              />
              
              {newMessage.length > 0 && (
                <div className="absolute bottom-2 right-3 text-xs text-gray-400">
                  {newMessage.length}
                </div>
              )}
            </div>
            
            <Button
              type="submit"
              disabled={sending || !newMessage.trim()}
              className="bg-gray-900 hover:bg-gray-800 self-end px-4 py-3 h-auto rounded-xl transition-all disabled:opacity-50"
            >
              {sending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </Button>
          </form>
          
          <p className="text-xs text-gray-400 mt-2 text-center">
            Enter for å sende • Shift+Enter for ny linje
          </p>
        </div>
      </div>
    </div>
  );
};

export default ChatMessages;
